import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { TRIAL_CONTEXT } from "../../data/icfSeed";

const STEPS = [
    { key: "extract", label: "Extract", sub: "Rules from Protocol + Regulation", to: "/icf/extract", idx: 1 },
    { key: "execute", label: "Execute", sub: "Compliance + Participant Impact", to: "/icf/execute", idx: 2 },
    { key: "explain", label: "Explain", sub: "Inspection-Ready Evidence", to: "/icf/explain", idx: 3 },
];

const TRIAL_STRIP = [
    { k: "Trial", v: TRIAL_CONTEXT.trial_id },
    { k: "Phase", v: TRIAL_CONTEXT.phase },
    { k: "Indication", v: TRIAL_CONTEXT.indication },
    { k: "Sponsor", v: TRIAL_CONTEXT.sponsor },
    { k: "Sites", v: TRIAL_CONTEXT.sites },
    { k: "Enrolled", v: `${TRIAL_CONTEXT.participants} pts` },
    { k: "Protocol", v: TRIAL_CONTEXT.protocol_version },
    { k: "Amendment", v: TRIAL_CONTEXT.pending_amendment },
    { k: "Master ICF", v: TRIAL_CONTEXT.master_icf },
    { k: "IRB", v: TRIAL_CONTEXT.irb },
];

export default function IcfLayout({ children }) {
    const loc = useLocation();
    const activeIdx = STEPS.findIndex((s) => loc.pathname.endsWith(`/${s.key}`));

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
            {/* Top header */}
            <header className="border-b border-hair bg-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center gap-4 flex-wrap">
                    <Link
                        to="/"
                        data-testid="icf-back-home"
                        className="text-[12px] mono uppercase tracking-wider text-neutral-500 hover:text-[#0A0A0A] inline-flex items-center gap-1.5"
                    >
                        <ArrowLeft size={12} /> Back to home
                    </Link>
                    <span className="text-neutral-300">·</span>
                    <div className="flex items-center gap-2">
                        <img
                            src="/audri-logo.png"
                            alt="AudRI"
                            className="w-6 h-6 rounded-sm"
                        />
                        <div className="heading font-bold text-[15px] leading-none">
                            audri
                        </div>
                        <span className="overline text-[9px] ml-1">
                            for ICF · Clinical Trials
                        </span>
                    </div>
                    <div className="ml-auto text-[11px] mono uppercase tracking-wider text-neutral-500">
                        Demo mode · pre-baked data
                    </div>
                </div>

                {/* Stepper */}
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-5">
                    <div className="grid grid-cols-3 border border-hair rounded-sm overflow-hidden">
                        {STEPS.map((s, i) => {
                            const done = activeIdx > i;
                            const active = activeIdx === i;
                            return (
                                <NavLink
                                    key={s.key}
                                    to={s.to}
                                    data-testid={`stepper-${s.key}`}
                                    className={`px-5 py-4 border-r border-hair last:border-r-0 flex items-start gap-3 transition-colors ${
                                        active
                                            ? "bg-white"
                                            : "bg-neutral-50 hover:bg-white"
                                    }`}
                                >
                                    <div
                                        className={`shrink-0 w-7 h-7 rounded-sm border flex items-center justify-center mono text-[12px] ${
                                            active
                                                ? "bg-[#C2410C] text-white border-[#C2410C]"
                                                : done
                                                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                                  : "border-hair text-neutral-400"
                                        }`}
                                    >
                                        {done ? <Check size={12} /> : s.idx}
                                    </div>
                                    <div className="min-w-0">
                                        <div
                                            className={`text-[13px] heading tracking-tight font-medium ${
                                                active ? "text-[#0A0A0A]" : "text-neutral-500"
                                            }`}
                                        >
                                            {s.label}
                                        </div>
                                        <div className="overline text-[9px] mt-1 truncate">
                                            {s.sub}
                                        </div>
                                    </div>
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            </header>

            {/* Trial context strip */}
            <div className="bg-white border-b border-hair">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-3 flex items-center gap-x-8 gap-y-2 overflow-x-auto">
                    {TRIAL_STRIP.map((f) => (
                        <div key={f.k} className="shrink-0">
                            <div className="overline text-[9px]">{f.k}</div>
                            <div className="text-[12px] mono text-neutral-700 mt-[2px]">
                                {f.v}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 md:px-10 py-8">
                {children}
            </main>
        </div>
    );
}

export const ScreenHeader = ({ title, subtitle, right }) => (
    <div className="mb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
                <div className="overline mb-3">ICF Compliance</div>
                <h1
                    className="heading text-3xl md:text-4xl font-medium tracking-tight leading-tight"
                    data-testid="icf-screen-title"
                >
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-[13px] text-neutral-600 mt-3 leading-relaxed max-w-3xl">
                        {subtitle}
                    </p>
                )}
            </div>
            {right}
        </div>
    </div>
);

export const StepCta = ({ to, label, testid }) => (
    <div className="mt-10 flex justify-end">
        <NavLink
            to={to}
            data-testid={testid}
            className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2.5 rounded-sm hover:bg-[#9A3412] transition-colors"
        >
            {label} →
        </NavLink>
    </div>
);
